import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { TokenAuthenticationEvents } from '@shared/models/token-authentication-events';
import { filter, Observable, switchMap, take } from 'rxjs';

/**
 * Token race condition interceptor
 */
@Injectable({ providedIn: 'root' })
export class TokenRaceConditionInterceptor implements HttpInterceptor {
  /**
   * White list of authentication URLs
   */
  private readonly authWhiteList = [
    'auth/access_token',
    'auth/id_token_authentication',
    'auth/refresh_token',
    'auth/logout',
    'openid-configuration',
    'csrf-token',
  ];

  /**
   * White list of URLs
   */
  private readonly staticWhiteList = [
    'static.caixa.celero.mobi',
  ];

  /**
   * Tokens handler service
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Interceptor
   *
   * @param request Request
   * @param next Next
   *
   * @returns HTTP event observable
   */
  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.authWhiteList.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    if (this.staticWhiteList.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    if (!this.tokens.accessToken) {
      return this.tokens?.eventsSubject$.pipe(
        take(1),
        filter(
          (e) =>
            e.event === TokenAuthenticationEvents.TOKEN_RECEIVED ||
            e.event === TokenAuthenticationEvents.TOKEN_EXCHANGED_FAILED
        ),
        switchMap((e) => {
          if (e.event === TokenAuthenticationEvents.TOKEN_EXCHANGED_FAILED) {
            throw new Error('Token exchange failed');
          }
          return this.intercept(request, next);
        })
      );
    }

    return next.handle(request);
  }
}
